import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Key, Shield, Loader2, Eye, EyeOff, Check, AlertTriangle, Globe, Lock } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getToken } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_REGIONS = [
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'IRE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'USA', name: 'United States', flag: '🇺🇸' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'AUS', name: 'Australia', flag: '🇦🇺' },
];

export default function SettingsPage() {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Form states
    const [openaiKey, setOpenaiKey] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [dailyLimit, setDailyLimit] = useState('');
    const [weeklyLimit, setWeeklyLimit] = useState('');
    const [selfExclude, setSelfExclude] = useState(false);
    const [selectedRegions, setSelectedRegions] = useState(['UK']);
    
    // Password change states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: { pathname: '/settings' } } });
            return;
        }

        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API}/settings`);
                setSettings(response.data);
                setDailyLimit(response.data.daily_limit?.toString() || '');
                setWeeklyLimit(response.data.weekly_limit?.toString() || '');
                setSelectedRegions(response.data.regions || ['UK']);
            } catch (error) {
                console.error('Failed to fetch settings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [isAuthenticated, navigate]);

    const handleRegionToggle = (regionCode) => {
        setSelectedRegions(prev => {
            if (prev.includes(regionCode)) {
                // Don't allow removing all regions
                if (prev.length === 1) {
                    toast.error('You must have at least one region selected');
                    return prev;
                }
                return prev.filter(r => r !== regionCode);
            } else {
                return [...prev, regionCode];
            }
        });
    };

    const handleSaveRegions = async () => {
        setSaving(true);
        try {
            const token = getToken();
            await axios.put(`${API}/settings`, {
                regions: selectedRegions
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Racing regions updated!');
            // Store in localStorage for non-logged in users too
            localStorage.setItem('racewise_regions', JSON.stringify(selectedRegions));
        } catch (error) {
            toast.error('Failed to update regions');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Please fill in all password fields');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }

        setChangingPassword(true);
        try {
            const token = getToken();
            await axios.post(`${API}/auth/change-password`, {
                current_password: currentPassword,
                new_password: newPassword
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Password changed successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    const handleSaveApiKey = async () => {
        if (!openaiKey.trim()) {
            toast.error('Please enter an API key');
            return;
        }

        setSaving(true);
        try {
            await axios.put(`${API}/settings`, {
                openai_api_key: openaiKey
            });
            toast.success('OpenAI API key saved!');
            setSettings(prev => ({ ...prev, openai_api_key_set: true }));
            setOpenaiKey('');
        } catch (error) {
            toast.error('Failed to save API key');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveLimits = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings`, {
                daily_limit: dailyLimit ? parseFloat(dailyLimit) : null,
                weekly_limit: weeklyLimit ? parseFloat(weeklyLimit) : null
            });
            toast.success('Limits updated!');
        } catch (error) {
            toast.error('Failed to update limits');
        } finally {
            setSaving(false);
        }
    };

    const handleSelfExclude = async () => {
        if (!selfExclude) return;
        
        const confirm = window.confirm(
            'Are you sure you want to self-exclude for 24 hours? You will not be able to place bets during this time.'
        );
        
        if (!confirm) {
            setSelfExclude(false);
            return;
        }

        setSaving(true);
        try {
            const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
            await axios.put(`${API}/settings`, {
                self_exclusion_until: until
            });
            toast.success('Self-exclusion activated for 24 hours');
        } catch (error) {
            toast.error('Failed to activate self-exclusion');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="container-app py-8 flex items-center justify-center min-h-[50vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="font-heading text-3xl font-bold mb-2 page-title">Settings</h1>
                    <p className="text-muted-foreground mb-8">
                        Manage your account and API integrations
                    </p>

                    <div className="space-y-6">
                        {/* Racing Regions */}
                        <Card data-testid="regions-settings-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Globe className="h-5 w-5" />
                                    Racing Regions
                                </CardTitle>
                                <CardDescription>
                                    Select which racing regions to display on the homepage
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {AVAILABLE_REGIONS.map(region => (
                                        <div
                                            key={region.code}
                                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                                selectedRegions.includes(region.code)
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border hover:border-muted-foreground'
                                            }`}
                                            onClick={() => handleRegionToggle(region.code)}
                                            data-testid={`region-${region.code}`}
                                        >
                                            <Checkbox
                                                checked={selectedRegions.includes(region.code)}
                                                onCheckedChange={() => handleRegionToggle(region.code)}
                                            />
                                            <span className="text-xl">{region.flag}</span>
                                            <span className="text-sm font-medium">{region.name}</span>
                                        </div>
                                    ))}
                                </div>
                                
                                <Button 
                                    onClick={handleSaveRegions}
                                    disabled={saving}
                                    className="w-full"
                                    data-testid="save-regions"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Save Region Preferences
                                </Button>
                                
                                <p className="text-xs text-muted-foreground text-center">
                                    Selected regions will appear on your homepage. You can also filter by region on the Races page.
                                </p>
                            </CardContent>
                        </Card>

                        <Separator />

                        {/* Change Password */}
                        <Card data-testid="password-settings-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lock className="h-5 w-5" />
                                    Change Password
                                </CardTitle>
                                <CardDescription>
                                    Update your account password
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="current-password">Current Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="current-password"
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            placeholder="Enter current password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            data-testid="current-password-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-0 top-0 h-full px-3"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        >
                                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="new-password">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="new-password"
                                            type={showNewPassword ? 'text' : 'password'}
                                            placeholder="Enter new password (min 6 characters)"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            data-testid="new-password-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-0 top-0 h-full px-3"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                        >
                                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        placeholder="Confirm new password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        data-testid="confirm-password-input"
                                    />
                                </div>
                                
                                <Button 
                                    onClick={handleChangePassword} 
                                    disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                                    className="w-full"
                                    data-testid="change-password-btn"
                                >
                                    {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Change Password
                                </Button>
                            </CardContent>
                        </Card>

                        <Separator />

                        {/* OpenAI API Key */}
                        <Card data-testid="openai-settings-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Key className="h-5 w-5" />
                                    OpenAI API Key
                                </CardTitle>
                                <CardDescription>
                                    Add your OpenAI API key to enable AI-powered race analysis
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {settings?.openai_api_key_set ? (
                                    <div className="flex items-center gap-2 text-sm text-primary">
                                        <Check className="h-4 w-4" />
                                        API key is configured ({settings.openai_api_key_masked})
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <AlertTriangle className="h-4 w-4" />
                                        No API key configured
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="openai-key">
                                        {settings?.openai_api_key_set ? 'Update API Key' : 'Enter API Key'}
                                    </Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Input
                                                id="openai-key"
                                                type={showKey ? 'text' : 'password'}
                                                placeholder="sk-..."
                                                value={openaiKey}
                                                onChange={(e) => setOpenaiKey(e.target.value)}
                                                data-testid="openai-key-input"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute right-0 top-0 h-full px-3"
                                                onClick={() => setShowKey(!showKey)}
                                            >
                                                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                        <Button 
                                            onClick={handleSaveApiKey} 
                                            disabled={saving}
                                            data-testid="save-openai-key"
                                        >
                                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                        </Button>
                                    </div>
                                </div>
                                
                                <p className="text-xs text-muted-foreground">
                                    Get your API key from{' '}
                                    <a 
                                        href="https://platform.openai.com/api-keys" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="underline"
                                    >
                                        OpenAI Platform
                                    </a>
                                    . Your key is stored securely and never shared.
                                </p>
                            </CardContent>
                        </Card>

                        <Separator />

                        {/* Responsible Gambling */}
                        <Card data-testid="gambling-settings-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="h-5 w-5" />
                                    Responsible Gambling
                                </CardTitle>
                                <CardDescription>
                                    Set limits to help manage your betting activity
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="daily-limit">Daily Limit (£)</Label>
                                        <Input
                                            id="daily-limit"
                                            type="number"
                                            placeholder="e.g., 50"
                                            min="0"
                                            value={dailyLimit}
                                            onChange={(e) => setDailyLimit(e.target.value)}
                                            data-testid="daily-limit-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="weekly-limit">Weekly Limit (£)</Label>
                                        <Input
                                            id="weekly-limit"
                                            type="number"
                                            placeholder="e.g., 200"
                                            min="0"
                                            value={weeklyLimit}
                                            onChange={(e) => setWeeklyLimit(e.target.value)}
                                            data-testid="weekly-limit-input"
                                        />
                                    </div>
                                </div>
                                
                                <Button 
                                    variant="outline" 
                                    onClick={handleSaveLimits}
                                    disabled={saving}
                                    data-testid="save-limits"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Save Limits
                                </Button>

                                <Separator />

                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <Label>Self-Exclusion (24 hours)</Label>
                                        <p className="text-sm text-muted-foreground">
                                            Temporarily block yourself from placing bets
                                        </p>
                                    </div>
                                    <Switch
                                        checked={selfExclude}
                                        onCheckedChange={(checked) => {
                                            setSelfExclude(checked);
                                            if (checked) handleSelfExclude();
                                        }}
                                        data-testid="self-exclude-switch"
                                    />
                                </div>

                                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                                    <p className="font-medium mb-2">Need support?</p>
                                    <p className="text-muted-foreground">
                                        If you're concerned about your gambling, visit{' '}
                                        <a 
                                            href="https://www.begambleaware.org" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="underline"
                                        >
                                            BeGambleAware.org
                                        </a>
                                        {' '}or call the National Gambling Helpline: 0808 8020 133
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
